import {
    FieldType,
    ResolveCloseButtonTextParams,
    ResolveConfirmButtonTextParams,
    ResolveModalTitleParams,
} from './types';

/**
 * Определяет префикс телефона на основе введенного значения
 * @param value - введенное значение телефона
 * @param currentPhoneDigits - текущий префикс
 * @returns новый префикс телефона
 */
export const determinePhonePrefix = (value: string, currentPhoneDigits: string): string => {
    if (currentPhoneDigits !== '') return currentPhoneDigits;

    if (value.startsWith('79')) {
        return '79';
    } else if (value.startsWith('89')) {
        return '89';
    } else if (value.startsWith('+79')) {
        return '+79';
    } else if (value.startsWith('9')) {
        return '9';
    } else if (value.startsWith('+7') && value.length >= 3 && value[2] === '9') {
        return '+79';
    }

    return '';
};

/**
 * Форматирует номер телефона в читаемый вид с скобками и дефисами
 * @param value - введенное значение телефона
 * @param fieldType - тип поля (phone/email)
 * @returns отформатированный номер телефона
 */
export const formatPhoneNumberWithMask = (value: string, fieldType: FieldType = FieldType.EMAIL): string => {
    const digits = value.replace(/\D/g, '');

    if (digits.length === 0) return '';

    if ((fieldType === FieldType.PHONE || fieldType === FieldType.CONTACT_PHONE) && digits.length > 11) {
        return value;
    }

    let phoneNumber = '';

    if (digits.length >= 1 && digits[0] === '9') {
        phoneNumber = '+7' + digits;
    } else if (
        digits.length >= 2 &&
        ((digits[0] === '7' && digits[1] === '9') || (digits[0] === '8' && digits[1] === '9'))
    ) {
        phoneNumber = '+7' + digits.substring(1);
    } else if (value.startsWith('+79')) {
        phoneNumber = value;
    } else if (value.startsWith('+7') && digits.length >= 2 && digits[1] === '9') {
        phoneNumber = value;
    } else {
        return value;
    }

    const cleanDigits = phoneNumber.replace(/\D/g, '');

    if (cleanDigits.length === 0) return '';
    if (cleanDigits.length === 1) return '+7';
    if (cleanDigits.length === 2) return '+7 (' + cleanDigits[1];
    if (cleanDigits.length === 3) return '+7 (' + cleanDigits[1] + cleanDigits[2];
    if (cleanDigits.length === 4) return '+7 (' + cleanDigits[1] + cleanDigits[2] + cleanDigits[3];
    if (cleanDigits.length === 5)
        return '+7 (' + cleanDigits[1] + cleanDigits[2] + cleanDigits[3] + ') ' + cleanDigits[4];
    if (cleanDigits.length === 6)
        return '+7 (' + cleanDigits[1] + cleanDigits[2] + cleanDigits[3] + ') ' + cleanDigits[4] + cleanDigits[5];
    if (cleanDigits.length === 7)
        return (
            '+7 (' +
            cleanDigits[1] +
            cleanDigits[2] +
            cleanDigits[3] +
            ') ' +
            cleanDigits[4] +
            cleanDigits[5] +
            cleanDigits[6]
        );
    if (cleanDigits.length === 8)
        return (
            '+7 (' +
            cleanDigits[1] +
            cleanDigits[2] +
            cleanDigits[3] +
            ') ' +
            cleanDigits[4] +
            cleanDigits[5] +
            cleanDigits[6] +
            '-' +
            cleanDigits[7]
        );
    if (cleanDigits.length === 9)
        return (
            '+7 (' +
            cleanDigits[1] +
            cleanDigits[2] +
            cleanDigits[3] +
            ') ' +
            cleanDigits[4] +
            cleanDigits[5] +
            cleanDigits[6] +
            '-' +
            cleanDigits[7] +
            cleanDigits[8]
        );
    if (cleanDigits.length === 10)
        return (
            '+7 (' +
            cleanDigits[1] +
            cleanDigits[2] +
            cleanDigits[3] +
            ') ' +
            cleanDigits[4] +
            cleanDigits[5] +
            cleanDigits[6] +
            '-' +
            cleanDigits[7] +
            cleanDigits[8] +
            '-' +
            cleanDigits[9]
        );
    if (cleanDigits.length === 11)
        return (
            '+7 (' +
            cleanDigits[1] +
            cleanDigits[2] +
            cleanDigits[3] +
            ') ' +
            cleanDigits[4] +
            cleanDigits[5] +
            cleanDigits[6] +
            '-' +
            cleanDigits[7] +
            cleanDigits[8] +
            '-' +
            cleanDigits[9] +
            cleanDigits[10]
        );

    return phoneNumber;
};

export const checkPhoneLength = (value: string, fieldType: FieldType): boolean => {
    const digitsCount = value.replace(/\D/g, '').length;

    return !((fieldType === FieldType.PHONE || fieldType === FieldType.CONTACT_PHONE) && digitsCount > 11);
};

export const isLikelyAutofill = (newValue: string, inputValue: string): boolean => {
    return (
        Math.abs(newValue.length - inputValue.length) > 1 ||
        (newValue.length >= 10 && /^\+?[0-9\s\-()]+$/.test(newValue))
    );
};

export const getInputTypeFromUtils = (forcedFieldType: FieldType): string => {
    if (forcedFieldType === FieldType.PHONE || forcedFieldType === FieldType.CONTACT_PHONE) {
        return 'tel';
    }
    if (forcedFieldType === FieldType.EMAIL || forcedFieldType === FieldType.CONTACT_EMAIL) {
        return 'email';
    }

    return 'text';
};

export const getDefaultLabelFromUtils = (forcedFieldType: FieldType): string => {
    if (forcedFieldType === 'phone') {
        return 'Номер телефона';
    }
    if (forcedFieldType === 'email' || forcedFieldType === 'contact_email') {
        return 'Email';
    }

    if (forcedFieldType === 'contact_phone') {
        return 'Контактный телефон';
    }

    return 'Номер телефона или email';
};

export const isPhoneFormat = (value: string): boolean => {
    if (!/^[0-9+\-()\s]*$/.test(value)) {
        return false;
    }

    if (value.startsWith('+7 (') && value.includes(')')) {
        return true;
    }

    if (value.length > 0) {
        const digits = value.replace(/\D/g, '');
        if (
            (digits.length >= 1 && digits[0] === '9') ||
            (digits.length >= 2 && digits[0] === '7' && digits[1] === '9') ||
            (digits.length >= 2 && digits[0] === '8' && digits[1] === '9') ||
            (value.startsWith('+7') && digits.length >= 2 && digits[1] === '9')
        ) {
            return true;
        }
    }

    return false;
};

export const determineFieldType = (value: string): FieldType => {
    if (isPhoneFormat(value)) {
        return FieldType.PHONE;
    }

    return FieldType.EMAIL;
};

export const resolveModalTitle = ({
    isCodeStep,
    isRegistrationForm,
    isSignIn,
}: ResolveModalTitleParams): string | undefined => {
    if (isCodeStep) {
        return 'Введите код';
    }

    if (isRegistrationForm) {
        return 'Регистрация';
    }

    if (isSignIn) {
        return 'Вход или регистрация';
    }

    return undefined;
};

export const resolveConfirmButtonText = ({
    isLoading,
    isSignIn,
    isRegistrationForm,
    isCodeStep,
    isCanSendAgain,
}: ResolveConfirmButtonTextParams): string => {
    if (isLoading) {
        return '';
    }

    if (isSignIn) {
        return 'Продолжить';
    }

    if (isRegistrationForm) {
        return 'Зарегистрироваться';
    }

    if (isCodeStep && isCanSendAgain) {
        return 'Получить новый код';
    }

    return '';
};

export const resolveCloseButtonText = ({
    isLoading,
    isRegistrationForm,
    isSendCodeByPhone,
    isSendCodeByEmail,
    isCanSendAgain,
    isLogin,
}: ResolveCloseButtonTextParams): string | undefined => {
    if (isLoading) {
        return undefined;
    }

    if (isRegistrationForm) {
        return 'Уже есть аккаунт';
    }

    if (isSendCodeByPhone && isCanSendAgain) {
        return isLogin ? 'Войти по email' : 'Подтвердить по email';
    }

    if (isSendCodeByEmail && isCanSendAgain) {
        return isLogin ? 'Войти по номеру' : 'Подтвердить по номеру';
    }

    return undefined;
};
